import { Button, Form, Input, Modal, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import React from 'react';

import { useMaintanceToDo } from 'providers/MaintanceToDoProvider';
import Signature from 'components/global/Form/Signature';
import { useNavigate } from 'react-router-dom';
import paths from 'assets/routes/paths';
import { useSolicitudesMantencion } from '../../providers/SolicitudesMantencionProvider';
import { captureGps } from 'hooks/useVisita';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { saveCierre } from 'utils/offlineCierreStore';

/**
 * Arma el payload de cierre mezclando los values del form con las coordenadas GPS.
 * Exportada para que los tests puedan ejercitar la lógica pura sin montar el componente.
 *
 * @param {object} values - Campos del formulario (name_recep, cargo_recep, etc.)
 * @param {{ lat: number|null, lng: number|null }} gps
 * @returns {object}
 */
export function buildFinishPayload(values, gps) {
  return { ...values, lat: gps.lat, lng: gps.lng };
}

/**
 * Arma el registro que se encola en `offlineCierreStore` cuando se finaliza
 * sin conexión: mismos datos del comprobante (recepcionista + GPS + refs de
 * firma agnósticas — S3 key u localId, ver Signature) más el estado del
 * encolado (PENDIENTE, finish aún no ejecutado contra el backend).
 *
 * @param {object} values - Campos del formulario (name_recep, cargo_recep, firma_spec, firma_recep)
 * @param {{ lat: number|null, lng: number|null }} gps
 * @returns {object}
 */
export function construirCierreOffline(values, gps) {
  return {
    name_recep: values.name_recep,
    cargo_recep: values.cargo_recep,
    lat: gps.lat,
    lng: gps.lng,
    fecha_finalizacion: new Date().toISOString(),
    firmaSpecRef: values.firma_spec,
    firmaRecepRef: values.firma_recep,
    status: 'PENDIENTE',
    finishDone: false,
    errorMotivo: null,
  };
}

/**
 * Orquesta el cierre de la mantención según haya o no conexión. Extraída del
 * componente (recibe sus dependencias por parámetro) para poder testear la
 * bifurcación offline/online sin montar el formulario completo — montar
 * `Signature` requiere interactuar con un canvas que jsdom no reproduce de
 * forma confiable (mismo motivo que `persistirFirma` en `Signature.jsx`).
 *
 * Offline + gate OK: encola el cierre en `offlineCierreStore` (status
 * PENDIENTE, finishDone false) y NO llama a finish/send — el daemon de
 * reintento se encarga al reconectar.
 * Offline + gate falla: `puedeCerrarOffline` ya notificó al usuario; no hace nada.
 * Online: comportamiento original intacto (finish + envío de PDF).
 *
 * @param {object} params
 * @param {object} params.values
 * @param {boolean} params.isOnline
 * @param {number|string} params.idMantencion
 * @param {() => boolean} params.puedeCerrarOffline
 * @param {(values: object) => Promise} params.handleFinishForm
 * @param {(payload: object) => Promise} params.handleSendPDF
 * @param {(path: string) => void} params.navigate
 * @param {(open: boolean) => void} [params.handleModal]
 */
export async function ejecutarSubmit({
  values,
  isOnline,
  idMantencion,
  puedeCerrarOffline,
  handleFinishForm,
  handleSendPDF,
  navigate,
  handleModal,
}) {
  if (!isOnline) {
    if (!puedeCerrarOffline()) return;
    const gps = await captureGps();
    saveCierre(idMantencion, construirCierreOffline(values, gps));
    message.success('Mantención guardada. Se enviará automáticamente cuando recuperes internet.');
    return navigate(paths.relative.specialist.root);
  }
  try {
    const gps = await captureGps();
    await handleFinishForm(buildFinishPayload(values, gps));
    if (handleModal) {
      handleModal(true);
    }
    await handleSendPDF({
      id_mantencion: idMantencion,
    });
    return navigate(paths.relative.specialist.root);
  } catch (error) {
    // Gate de arrastre (409): quedan hallazgos previos sin decidir. Se avisa
    // con un modal; el técnico los ubica por el badge "⚠ hallazgo previo sin
    // resolver" en cada ítem.
    if (error?.response?.status === 409) {
      Modal.warning({
        title: 'Hallazgos previos sin resolver',
        content:
          error?.response?.data?.message ||
          'Quedan hallazgos de visitas anteriores sin decidir. Revisa los ítems marcados y elige qué pasa con cada uno (sigue, resuelto o no pudiste verificarlo) antes de finalizar.',
        okText: 'Entendido',
      });
      return;
    }
    // Otro error: fallo silencioso — el técnico ya ve el formulario abierto.
  }
}

function FinishMaintanceForm({ handleModal, idMantencion }) {
  const [form] = useForm();
  const { handleFinishForm, puedeCerrarOffline } = useMaintanceToDo();
  const navigate = useNavigate();
  const { handleSendPDF } = useSolicitudesMantencion();
  const isOnline = useOnlineStatus();
  const submit = async () => {
    const values = form.getFieldsValue();
    return ejecutarSubmit({
      values,
      isOnline,
      idMantencion,
      puedeCerrarOffline,
      handleFinishForm,
      handleSendPDF,
      navigate,
      handleModal,
    });
  };
  return (
    <>
      <Form
        form={form}
        onFinish={submit}
        requiredMark={false}
        initialValues={{ id_mantencion: idMantencion }}
      >
        <Form.Item
          name="id_mantencion"
          label="Nombre del recepcionista"
          hidden
          labelCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="name_recep"
          label="Nombre del recepcionista"
          rules={[
            {
              required: true,
              message: 'Debe ingresar el nombre de quien recibe el comprobante',
            },
          ]}
          labelCol={{ span: 24 }}
        >
          <Input placeholder="Ingresar nombre de recepcionista" />
        </Form.Item>
        <Form.Item
          label="Cargo de recepción"
          name="cargo_recep"
          labelCol={{ span: 24 }}
          rules={[
            {
              required: true,
              message: 'El cargo del recepcionista es obligatorio',
            },
          ]}
        >
          <Input placeholder="Cargo de quien recibe" />
        </Form.Item>
        <Form.Item
          label="Firma recepcionista"
          name="firma_recep"
          rules={[
            {
              required: true,
              message: 'La firma del recepcionista es obligatoria',
            },
          ]}
          labelCol={{ span: 24 }}
        >
          <Signature mantencionId={idMantencion} role="recep" />
        </Form.Item>
        <Form.Item
          label="Firma Especialista"
          name="firma_spec"
          rules={[
            {
              required: true,
              message: 'La firma del especialista es obligatoria',
            },
          ]}
          labelCol={{ span: 24 }}
        >
          <Signature mantencionId={idMantencion} role="spec" />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" type="primary">
            Finalizar
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}

export default FinishMaintanceForm;
